/**
 * ソロ・ドリル レイアウト定義＋合否判定（自前・GPL 非依存・依存ゼロ系）。
 * 設計: knowledge/architecture/solo-drill-daily-challenge.md（F-30）
 * 配置カタログ: knowledge/architecture/solo-drill-catalog.md
 *
 * 方針:
 *   - 配置は「ダイヤ座標 (l, s)」で記述し、盤面定数でメートルへ変換する（作図容易・カタログと一致）。
 *   - メートル化後は seatResolvedBalls（rack-layout・M-05）で重なりを解消してエンジンへ渡す前提を満たす。
 *   - 合否判定 judgeDrillShot は ShotObservation（M-04・型のみ参照）を読む「観測者」。
 *     ルール reducer は置き換えない（ソロの継続挙動・F-05 の純粋性に非影響）。
 *
 * GPL 非依存: 物理/描画/three へ非依存。球半径 R はローカル定数（rack-layout と同方式）。
 * engine から import しないのは循環参照回避＋GPL 非依存維持（CLAUDE.md R-2）のため。
 * R / 盤面半幅は tests/drill-layouts.test.ts が engine 値と一致することを検証する（ドリフト検知）。
 */

import type { ShotObservation } from './rules/types.js';
import { seatResolvedBalls, type ResolvedBall } from './rack-layout.js';

// --- 盤面定数（ボール中心の可動域・メートル） ---
// engine/constants.ts R と engine/tablegeometry.ts tableX/tableY に一致（drift test で検証）。
const R = 0.03275;
const TABLE_HALF_X = 43 * R; // ボール中心の長辺方向半幅（cushion nose は 44R）
const TABLE_HALF_Y = 21 * R; // ボール中心の短辺方向半幅（cushion nose は 22R）

// --- 型 ---

// 級位の序列（易→難）: C → B → SB → A → SA（日本ビリヤードの標準級位。SB は B と A の間）。
export type DrillRank = 'C' | 'B' | 'SB' | 'A' | 'SA';

/** 合否ルール。Phase 1 は clearAll（取り切り）のみ実装（landInZone は Phase 2）。 */
export type DrillPassRule = 'clearAll' | 'landInZone';

export interface DrillRankDef {
  readonly rank: DrillRank;
  /** 表示名（例: 'C級'）。表示はここを一次情報にする（ハードコード散在禁止）。 */
  readonly label: string;
  /** 昇格順（0 = 最易・最初から開放）。 */
  readonly order: number;
  /** 球数下限（1 球配置を禁止＝drill-no-single-ball）。 */
  readonly minBalls: number;
  /** ショット上限の余裕分（許容ミス数）。maxShots = 球数 + extraShots（drill-pass-model）。 */
  readonly extraShots: number;
  /** このランクを解放するのに必要な「直前ランクのクリア数」。order 0 は無条件開放。 */
  readonly unlockClearsNeeded: number;
}

/** ダイヤ座標の的玉 1 個（番号＋盤面位置）。 */
export interface DrillBallSpec {
  readonly number: number;
  /** 長辺ダイヤ 0（ヘッド）〜8（フット）。 */
  readonly l: number;
  /** 短辺ダイヤ 0〜4。 */
  readonly s: number;
}

/** 1 ドリル配置。 */
export interface DrillLayout {
  readonly id: string;
  readonly rank: DrillRank;
  readonly seq: number;
  readonly name: string;
  readonly cue: { readonly l: number; readonly s: number };
  readonly balls: readonly DrillBallSpec[];
  /** true なら番号昇順に取る制約（9 ボール準拠）。 */
  readonly targetOrder: boolean;
  readonly pass: DrillPassRule;
  /** ショット上限の個別上書き（未指定なら 球数 + ランクの extraShots）。 */
  readonly maxShots?: number;
}

/** メートルへ解決した配置（エンジン投入用）。 */
export interface ResolvedDrill {
  readonly cue: { readonly x: number; readonly y: number };
  readonly balls: readonly ResolvedBall[];
  /** 取り切るべき番号順（targetOrder=true なら昇順）。 */
  readonly order: readonly number[];
}

/** 1 ショットの素の合否（取り切り進行）。ショット上限は stepDrill が別途扱う。 */
export type DrillVerdict = 'progress' | 'miss' | 'cleared';

/** セッションのラン状態（ショット上限制）。 */
export interface DrillRunState {
  /** 残存的玉番号。 */
  readonly remaining: readonly number[];
  /** 消費ショット数。 */
  readonly shotsUsed: number;
  /** ミス（非合法・無得点）回数。 */
  readonly misses: number;
}

/** 1 ショット適用後のセッション結果。 */
export type DrillOutcome =
  | 'progress' // 取り切り進行中（上限内）
  | 'miss' // ミスしたが続行（上限内）
  | 'cleared' // 取り切り達成（ミスあり）
  | 'clearedNoMiss' // ノーミス取り切り達成（称号対象）
  | 'failed'; // ショット上限超過 → 最初からやり直し

// --- ランク定義 ---

export const DRILL_RANKS: readonly DrillRankDef[] = [
  { rank: 'C', label: 'C級', order: 0, minBalls: 3, extraShots: 2, unlockClearsNeeded: 0 },
  { rank: 'B', label: 'B級', order: 1, minBalls: 3, extraShots: 2, unlockClearsNeeded: 7 },
  { rank: 'SB', label: 'SB級', order: 2, minBalls: 4, extraShots: 3, unlockClearsNeeded: 7 },
  { rank: 'A', label: 'A級', order: 3, minBalls: 5, extraShots: 3, unlockClearsNeeded: 6 },
  { rank: 'SA', label: 'SA級', order: 4, minBalls: 6, extraShots: 4, unlockClearsNeeded: 6 },
];

export const getRankDef = (rank: DrillRank): DrillRankDef => {
  const def = DRILL_RANKS.find((d) => d.rank === rank);
  if (!def) throw new Error(`drill: unknown rank ${rank}`);
  return def;
};

// --- ドリル定義ヘルパ（簡潔記述用・class ではない純関数） ---

const b = (number: number, l: number, s: number): DrillBallSpec => ({ number, l, s });

interface MkOpts {
  readonly targetOrder?: boolean;
  readonly pass?: DrillPassRule;
  readonly maxShots?: number;
}

const mk = (
  rank: DrillRank,
  seq: number,
  name: string,
  cue: readonly [number, number],
  balls: readonly DrillBallSpec[],
  opts: MkOpts = {},
): DrillLayout => ({
  id: `${rank.toLowerCase()}-${String(seq).padStart(2, '0')}`,
  rank,
  seq,
  name,
  cue: { l: cue[0], s: cue[1] },
  balls,
  targetOrder: opts.targetOrder ?? true,
  pass: opts.pass ?? 'clearAll',
  ...(opts.maxShots !== undefined ? { maxShots: opts.maxShots } : {}),
});

// =====================================================================
//  配置データ（C〜SA 各 10・計 50）。座標は solo-drill-catalog.md 準拠の草案。
//  ⚠ キック/バンク/コンビ等の「特殊技術が必然」になる厳密幾何は後続パスで微調整する。
//     本データは「球数下限・盤内・重なりなし」の不変条件をテストで保証した初版。
// =====================================================================

// --- C 級: 操作習熟＋連続ポット（最低 3 球） ---
const DRILLS_C: readonly DrillLayout[] = [
  mk('C', 1, '直線ライン3球', [0.8, 2.0], [b(1, 2.5, 2.0), b(2, 4.5, 2.0), b(3, 6.5, 2.0)]),
  mk('C', 2, 'ストップ連取', [2.5, 2.0], [b(1, 5.0, 1.2), b(2, 6.0, 2.0), b(3, 5.0, 2.8)]),
  mk('C', 3, 'フォロー連取（押し）', [1.2, 2.0], [b(1, 3.0, 2.0), b(2, 5.0, 2.0), b(3, 6.5, 3.0)]),
  mk('C', 4, 'ドロー連取（引き）', [7.0, 2.0], [b(1, 5.5, 2.0), b(2, 3.5, 2.0), b(3, 2.2, 3.0)]),
  mk('C', 5, '押し引き交互', [1.5, 2.0], [b(1, 3.0, 1.5), b(2, 5.5, 2.5), b(3, 3.5, 3.0), b(4, 6.0, 1.5)]),
  mk('C', 6, '易しいカット3球', [2.0, 1.5], [b(1, 5.0, 1.0), b(2, 6.0, 2.2), b(3, 4.5, 3.0)]),
  mk('C', 7, 'サイド/コーナー混在', [3.5, 2.0], [b(1, 4.0, 3.0), b(2, 7.0, 3.2), b(3, 7.0, 0.8)]),
  mk('C', 8, '撞点パッド慣熟', [2.5, 2.0], [b(1, 4.0, 2.0), b(2, 5.5, 1.3), b(3, 5.5, 2.7)]),
  mk('C', 9, 'パワー較正3球', [1.0, 2.5], [b(1, 3.0, 2.5), b(2, 5.0, 2.5), b(3, 7.0, 2.5)]),
  mk('C', 10, 'フットスポット起点', [3.0, 2.0], [b(1, 6.0, 2.0), b(2, 5.0, 3.0), b(3, 4.5, 1.2), b(4, 6.8, 3.0)]),
];

// --- B 級: 基本ポジション（3〜4 球） ---
const DRILLS_B: readonly DrillLayout[] = [
  mk('B', 1, '止めで横移動', [4.5, 2.0], [b(1, 6.0, 1.2), b(2, 6.0, 2.8), b(3, 3.0, 2.0)]),
  mk('B', 2, '押しで前進ポジション', [2.5, 2.0], [b(1, 4.0, 2.0), b(2, 6.0, 1.3), b(3, 7.0, 2.2)]),
  mk('B', 3, '引きで戻すポジション', [7.0, 2.0], [b(1, 6.0, 1.2), b(2, 4.0, 2.0), b(3, 2.2, 2.0)]),
  mk('B', 4, '1クッション出し', [5.0, 2.0], [b(1, 6.0, 2.0), b(2, 2.2, 3.0), b(3, 2.2, 1.0)]),
  mk('B', 5, 'サイド→コーナー連携', [3.5, 2.0], [b(1, 4.0, 3.2), b(2, 7.0, 3.3), b(3, 1.0, 0.8)]),
  mk('B', 6, '4球ライン', [0.8, 2.0], [b(1, 2.0, 2.0), b(2, 3.8, 2.0), b(3, 5.6, 2.0), b(4, 7.2, 2.0)]),
  mk('B', 7, '緩いZポジション', [7.0, 2.0], [b(1, 6.0, 1.2), b(2, 4.0, 3.0), b(3, 2.0, 1.0), b(4, 4.0, 2.0)]),
  mk('B', 8, '角度を作る止め', [2.5, 1.5], [b(1, 5.0, 1.8), b(2, 6.2, 2.2), b(3, 4.0, 3.0)]),
  mk('B', 9, 'レール際の処理', [7.0, 1.5], [b(1, 6.0, 0.6), b(2, 4.0, 1.0), b(3, 2.5, 2.0)]),
  mk('B', 10, 'センター戻し', [7.0, 2.0], [b(1, 6.0, 1.5), b(2, 5.0, 3.0), b(3, 3.0, 1.5)], { pass: 'landInZone' }),
];

// --- SB 級: 角度とスピード制御（4〜5 球・B と A の間） ---
const DRILLS_SB: readonly DrillLayout[] = [
  mk('SB', 1, 'L字ドリル', [1.0, 1.0], [b(1, 2.0, 1.0), b(2, 4.0, 1.0), b(3, 6.0, 1.0), b(4, 6.0, 3.0)]),
  mk('SB', 2, 'ライン横断', [1.0, 2.5], [b(1, 2.0, 1.5), b(2, 3.4, 2.5), b(3, 4.8, 1.5), b(4, 6.2, 2.5), b(5, 7.0, 1.5)]),
  mk('SB', 3, 'サークル・ドリル', [4.0, 2.0], [b(1, 5.4, 2.0), b(2, 4.7, 3.2), b(3, 3.3, 3.2), b(4, 2.6, 2.0), b(5, 3.3, 0.8)]),
  mk('SB', 4, 'Mighty X', [4.0, 2.0], [b(1, 3.0, 1.0), b(2, 5.0, 3.0), b(3, 5.0, 1.0), b(4, 3.0, 3.0)]),
  mk('SB', 5, 'レール・ファースト出し', [4.0, 2.0], [b(1, 6.0, 0.7), b(2, 4.0, 3.2), b(3, 2.0, 1.0), b(4, 6.0, 3.0)]),
  mk('SB', 6, 'フォロー・ツー・レール', [1.2, 2.0], [b(1, 3.0, 2.0), b(2, 5.0, 2.5), b(3, 6.5, 1.5), b(4, 7.0, 3.0)]),
  mk('SB', 7, 'ドロー・アクロス', [4.0, 2.0], [b(1, 6.0, 1.0), b(2, 2.0, 3.0), b(3, 6.0, 3.0), b(4, 2.0, 1.0)]),
  mk('SB', 8, 'ランダム・スプレッド', [1.0, 2.0], [b(1, 2.4, 1.3), b(2, 5.1, 2.7), b(3, 3.6, 3.1), b(4, 6.4, 1.4), b(5, 4.2, 1.8)]),
  mk('SB', 9, 'ゾーン縛り連取', [1.5, 2.0], [b(1, 3.0, 2.0), b(2, 5.0, 1.5), b(3, 6.0, 3.0), b(4, 4.0, 3.0)], { pass: 'landInZone' }),
  mk('SB', 10, 'スピード階段（遠近）', [1.0, 2.0], [b(1, 2.0, 2.0), b(2, 7.0, 2.0), b(3, 3.0, 2.6), b(4, 6.0, 1.4), b(5, 4.0, 2.0)]),
];

// --- A 級: ひねり（english）・スロー（5〜6 球） ---
const DRILLS_A: readonly DrillLayout[] = [
  mk('A', 1, '外ひねりで伸ばす', [7.0, 1.5], [b(1, 6.0, 1.0), b(2, 4.0, 1.5), b(3, 5.5, 2.8), b(4, 3.0, 3.0), b(5, 2.0, 1.5)]),
  mk('A', 2, '内ひねりで締める', [1.0, 2.0], [b(1, 2.0, 1.5), b(2, 4.0, 1.2), b(3, 5.5, 2.0), b(4, 3.5, 3.0), b(5, 6.5, 3.0)]),
  mk('A', 3, 'スロー込み', [2.0, 2.4], [b(1, 5.0, 1.8), b(2, 6.2, 2.0), b(3, 4.0, 2.6), b(4, 2.6, 1.6), b(5, 3.4, 3.2)]),
  mk('A', 4, 'レールカット＋ひねり', [7.0, 2.0], [b(1, 6.5, 0.6), b(2, 4.5, 0.7), b(3, 2.5, 0.8), b(4, 5.0, 2.5), b(5, 3.0, 3.0)]),
  mk('A', 5, '2クッション・ポジション', [5.0, 1.6], [b(1, 6.0, 2.0), b(2, 3.0, 1.2), b(3, 5.0, 3.2), b(4, 2.0, 2.6), b(5, 6.5, 3.2)]),
  mk('A', 6, '3クッション・ポジション', [5.0, 2.4], [b(1, 6.0, 1.5), b(2, 4.0, 2.0), b(3, 5.5, 3.0), b(4, 2.5, 1.4), b(5, 3.2, 3.2), b(6, 6.8, 2.6)]),
  mk('A', 7, 'フリーズ・トゥ・レール', [6.8, 1.6], [b(1, 6.0, 0.12), b(2, 4.0, 0.12), b(3, 5.0, 2.4), b(4, 3.0, 1.6), b(5, 2.2, 3.0)]),
  mk('A', 8, 'ひねり量の較正', [1.0, 2.5], [b(1, 2.0, 2.0), b(2, 4.0, 2.0), b(3, 6.0, 2.0), b(4, 3.0, 3.0), b(5, 5.0, 1.0)], { pass: 'landInZone' }),
  mk('A', 9, '6球パターン走り', [1.0, 2.0], [b(1, 2.3, 1.4), b(2, 5.0, 2.6), b(3, 3.7, 3.1), b(4, 6.3, 1.5), b(5, 4.4, 1.7), b(6, 6.6, 3.2)]),
  mk('A', 10, 'サイド連続出し', [2.0, 2.0], [b(1, 4.0, 3.2), b(2, 5.5, 3.3), b(3, 2.5, 3.3), b(4, 4.0, 0.8), b(5, 6.0, 2.0)]),
];

// --- SA 級: トラブル解消・総合（6〜8 球） ---
const DRILLS_SA: readonly DrillLayout[] = [
  mk('SA', 1, 'クラスター割り出し', [2.0, 2.0], [b(1, 5.0, 2.0), b(2, 5.2, 2.05), b(3, 3.0, 1.5), b(4, 6.5, 3.0), b(5, 2.5, 3.0), b(6, 6.0, 1.0)]),
  mk('SA', 2, 'フリーズ球エスケープ', [6.0, 0.6], [b(1, 6.0, 0.12), b(2, 4.0, 2.0), b(3, 5.0, 3.0), b(4, 2.5, 1.5), b(5, 3.2, 3.2), b(6, 6.5, 2.5)]),
  mk('SA', 3, 'キック絡みの取り切り', [7.0, 2.0], [b(1, 1.5, 2.0), b(2, 4.0, 2.0), b(3, 5.5, 2.8), b(4, 3.0, 3.2), b(5, 6.5, 1.5), b(6, 6.0, 3.2)]),
  mk('SA', 4, 'バンク絡みの取り切り', [2.0, 2.0], [b(1, 6.0, 3.3), b(2, 4.5, 1.5), b(3, 5.8, 2.4), b(4, 3.0, 2.8), b(5, 6.5, 0.8), b(6, 2.5, 3.2)]),
  mk('SA', 5, 'コンビ絡みの取り切り', [2.0, 2.0], [b(1, 5.0, 2.0), b(2, 5.9, 2.4), b(3, 3.5, 1.4), b(4, 6.0, 3.2), b(5, 2.5, 2.8), b(6, 4.2, 3.2)]),
  mk('SA', 6, 'キャロム絡みの取り切り', [2.5, 2.0], [b(1, 4.5, 2.0), b(2, 5.6, 1.6), b(3, 6.2, 2.8), b(4, 3.0, 3.0), b(5, 6.5, 1.2), b(6, 2.5, 1.4)]),
  mk('SA', 7, 'トラブル先行の組み立て', [1.5, 2.0], [b(1, 4.0, 2.0), b(2, 4.2, 2.05), b(3, 6.0, 1.2), b(4, 6.0, 2.8), b(5, 2.5, 3.2), b(6, 5.0, 3.2), b(7, 3.0, 1.2)]),
  mk('SA', 8, 'セーフティ局面の判断', [4.0, 2.0], [b(1, 6.0, 1.5), b(2, 5.0, 3.0), b(3, 2.5, 1.5), b(4, 6.5, 2.8), b(5, 3.0, 3.2), b(6, 2.0, 2.4)], { pass: 'landInZone' }),
  mk('SA', 9, '9ボール・ミニ走り', [1.0, 2.0], [b(1, 2.2, 1.4), b(2, 5.0, 2.6), b(3, 3.6, 3.2), b(4, 6.4, 1.4), b(5, 4.3, 1.7), b(6, 6.6, 3.2), b(7, 2.6, 2.8), b(8, 7.2, 2.2)]),
  mk('SA', 10, '本日のお題・常設枠', [1.2, 2.5], [b(1, 2.5, 1.3), b(2, 5.2, 2.7), b(3, 3.8, 1.6), b(4, 6.4, 3.2), b(5, 4.5, 3.2), b(6, 6.8, 1.4), b(7, 2.4, 3.2), b(8, 5.6, 0.8)]),
];

export const DRILLS: readonly DrillLayout[] = [
  ...DRILLS_C,
  ...DRILLS_B,
  ...DRILLS_SB,
  ...DRILLS_A,
  ...DRILLS_SA,
];

// --- 解決（ダイヤ→メートル＋重なり解消） ---

/** ダイヤ座標 (l, s) をメートル（ボール中心）へ変換。 */
export const diamondToMeters = (l: number, s: number): { x: number; y: number } => ({
  x: -TABLE_HALF_X + (l / 8) * (2 * TABLE_HALF_X),
  y: -TABLE_HALF_Y + (s / 4) * (2 * TABLE_HALF_Y),
});

/**
 * ドリルをメートル配置へ解決する。
 * 手球を疑似球(番号 0)として的玉と一緒に seatResolvedBalls にかけ、重なりをゼロ保証してから分離する。
 */
export const resolveDrill = (layout: DrillLayout): ResolvedDrill => {
  const cueM = diamondToMeters(layout.cue.l, layout.cue.s);
  const raw: ResolvedBall[] = [
    { number: 0, x: cueM.x, y: cueM.y },
    ...layout.balls.map((ball) => {
      const m = diamondToMeters(ball.l, ball.s);
      return { number: ball.number, x: m.x, y: m.y };
    }),
  ];
  const seated = seatResolvedBalls(raw);
  const cueSeated = seated.find((sb) => sb.number === 0) ?? raw[0]!;
  const balls = seated.filter((sb) => sb.number !== 0);
  const numbers = layout.balls.map((ball) => ball.number);
  const order = layout.targetOrder ? [...numbers].sort((p, q) => p - q) : numbers;
  return { cue: { x: cueSeated.x, y: cueSeated.y }, balls, order };
};

// --- 合否判定（純粋・ShotObservation 観測者） ---

/**
 * 1 ショットの観測からドリル進行を評価する（純粋関数）。
 *
 * miss 条件（clearAll の 1 ショット判定・ショット上限は stepDrill が扱う）:
 *   - 手球スクラッチ / 的玉盤外
 *   - targetOrder=true: 最初の接触が最小番号でない（誤球）
 *   - targetOrder=false: 最初の接触が残存的玉でない
 *   - 残存的玉を 1 つも落とせなかった
 * すべて満たして 1 つ以上落とせば progress、残存が空になれば cleared。
 */
export const judgeDrillShot = (
  layout: DrillLayout,
  obs: ShotObservation,
  remainingBefore: readonly number[],
): { verdict: DrillVerdict; remainingAfter: readonly number[] } => {
  const remaining = [...remainingBefore];
  if (remaining.length === 0) return { verdict: 'cleared', remainingAfter: [] };

  if (obs.cueScratched || obs.objectBallOffTable) {
    // 反則（スクラッチ/的玉場外）。ただし潰した的玉は盤面から消えているので remaining からも除く
    // （盤面と同期）。これを怠ると「最後の球を入れてスクラッチ→残球が減らず・盤面は的玉ゼロで詰み」
    // になる（本番報告 2026-06-15）。残が空になれば stepDrill が達成判定（反則は miss 計上＝称号なし）。
    const after = remaining.filter((n) => !obs.pottedIds.includes(n));
    return { verdict: 'miss', remainingAfter: after };
  }

  const targets = new Set(remaining);
  if (layout.targetOrder) {
    const lowest = Math.min(...remaining);
    if (obs.firstContactId !== lowest) return { verdict: 'miss', remainingAfter: remaining };
  } else if (obs.firstContactId === null || !targets.has(obs.firstContactId)) {
    return { verdict: 'miss', remainingAfter: remaining };
  }

  const pottedTargets = obs.pottedIds.filter((id) => targets.has(id));
  if (pottedTargets.length === 0) return { verdict: 'miss', remainingAfter: remaining };

  const pottedSet = new Set(pottedTargets);
  const after = remaining.filter((n) => !pottedSet.has(n));
  return { verdict: after.length === 0 ? 'cleared' : 'progress', remainingAfter: after };
};

/** ドリルのショット上限（個別上書き優先・既定は 球数 + ランクの extraShots）。 */
export const drillMaxShots = (layout: DrillLayout): number =>
  layout.maxShots ?? layout.balls.length + getRankDef(layout.rank).extraShots;

/** 新規ランの初期状態（残=全的玉・ショット/ミス=0）。 */
export const initDrillRun = (layout: DrillLayout): DrillRunState => ({
  remaining: layout.balls.map((ball) => ball.number),
  shotsUsed: 0,
  misses: 0,
});

/**
 * 1 ショットをランへ適用する（純粋・ショット上限制＝drill-pass-model）。
 *
 * - 取り切り達成: ミス 0 なら 'clearedNoMiss'（称号対象）、ミスありなら 'cleared'（達成）。
 * - 未クリアで上限到達: 'failed'（呼び出し側が最初からやり直す）。
 * - それ以外: 'progress' / 'miss'（続行）。
 * ミスしても即終了しない（猶予内なら継続）。
 */
export const stepDrill = (
  layout: DrillLayout,
  obs: ShotObservation,
  run: DrillRunState,
  maxShots: number,
): { run: DrillRunState; outcome: DrillOutcome } => {
  const j = judgeDrillShot(layout, obs, run.remaining);
  const shotsUsed = run.shotsUsed + 1;
  const misses = run.misses + (j.verdict === 'miss' ? 1 : 0);
  const next: DrillRunState = { remaining: [...j.remainingAfter], shotsUsed, misses };
  if (next.remaining.length === 0) {
    return { run: next, outcome: misses === 0 ? 'clearedNoMiss' : 'cleared' };
  }
  if (shotsUsed >= maxShots) {
    return { run: next, outcome: 'failed' };
  }
  return { run: next, outcome: j.verdict === 'miss' ? 'miss' : 'progress' };
};

// --- ランク解放・進捗 ---

export const drillsByRank = (rank: DrillRank): readonly DrillLayout[] =>
  DRILLS.filter((d) => d.rank === rank);

/** 同ランク内で「現在の次（seq 昇順）」のドリルを返す。末尾・未登録なら null。
 *  次ドリル導線（クリア後の「▶ 次のドリル」）の遷移先決定に使う。 */
export const nextDrillInRank = (layout: DrillLayout): DrillLayout | null => {
  const inRank = [...drillsByRank(layout.rank)].sort((a, b) => a.seq - b.seq);
  const idx = inRank.findIndex((d) => d.id === layout.id);
  if (idx < 0 || idx + 1 >= inRank.length) return null;
  return inRank[idx + 1] ?? null;
};

/** クリア後の「▶ 次のドリル」遷移先を、ランクを跨いで決定する。
 *  優先順位:
 *    1. 同ランク内の seq 次（nextDrillInRank と同じ）
 *    2. 同ランクが全クリア済みなら、次に解放済みの上位ランクの先頭ドリル
 *    3. 該当なし（全ランククリア済み・上位未解放）なら null
 *  ランク跨ぎ進行（C 全達成 → B 先頭へ）を実現する遷移ヘルパ。 */
export const nextDrillAcrossRanks = (
  layout: DrillLayout,
  clearedIds: readonly string[],
): DrillLayout | null => {
  const inRank = nextDrillInRank(layout);
  if (inRank) return inRank;
  // 同ランク末尾。次に解放済みのランクの先頭ドリルへ進む（order 昇順で最初に見つかったもの）。
  const currentOrder = getRankDef(layout.rank).order;
  const upper = [...DRILL_RANKS]
    .filter((d) => d.order > currentOrder)
    .sort((a, b) => a.order - b.order);
  for (const def of upper) {
    if (!isRankUnlocked(def.rank, clearedIds)) continue;
    const head = [...drillsByRank(def.rank)].sort((a, b) => a.seq - b.seq)[0];
    if (head) return head;
  }
  return null;
};

/** 一覧を開いた時にアクティブにすべきランクを推定する。
 *  ・現在の rank が指定されており、そのランク内に未クリアが残るならそのまま
 *  ・全クリア済みなら、次に解放されている上位ランクへ移す
 *  ・上位もなければ rank をそのまま返す（最終ランクまで全クリア時）。 */
export const suggestActiveRank = (
  preferred: DrillRank,
  clearedIds: readonly string[],
): DrillRank => {
  const cleared = new Set(clearedIds);
  const hasRemaining = (rank: DrillRank): boolean =>
    drillsByRank(rank).some((d) => !cleared.has(d.id));
  if (isRankUnlocked(preferred, clearedIds) && hasRemaining(preferred)) return preferred;
  const order = getRankDef(preferred).order;
  const upper = [...DRILL_RANKS]
    .filter((d) => d.order > order)
    .sort((a, b) => a.order - b.order);
  for (const def of upper) {
    if (isRankUnlocked(def.rank, clearedIds) && hasRemaining(def.rank)) return def.rank;
  }
  return preferred;
};

/** クリア済みドリル ID 集合から、ランク別のクリア数を集計する。 */
export const clearedCountByRank = (
  clearedIds: readonly string[],
): Readonly<Record<DrillRank, number>> => {
  const cleared = new Set(clearedIds);
  const counts: Record<DrillRank, number> = { C: 0, B: 0, SB: 0, A: 0, SA: 0 };
  for (const d of DRILLS) {
    if (cleared.has(d.id)) counts[d.rank] += 1;
  }
  return counts;
};

/** ノーミス取り切り済みドリル ID 集合から、ランク別のノーミス達成数を集計する。
 *  drill-rank-no-miss（級の完全クリア＝全ドリルをノーミス）報酬の判定に使う。 */
export const noMissCountByRank = (
  noMissIds: readonly string[],
): Readonly<Record<DrillRank, number>> => {
  const noMiss = new Set(noMissIds);
  const counts: Record<DrillRank, number> = { C: 0, B: 0, SB: 0, A: 0, SA: 0 };
  for (const d of DRILLS) {
    if (noMiss.has(d.id)) counts[d.rank] += 1;
  }
  return counts;
};

/** ランクが解放済みか（order 0 は無条件・他は直前ランクの達成数で判定）。 */
export const isRankUnlocked = (rank: DrillRank, clearedIds: readonly string[]): boolean => {
  const def = getRankDef(rank);
  if (def.order === 0) return true;
  const prev = DRILL_RANKS.find((d) => d.order === def.order - 1);
  if (!prev) return true;
  const counts = clearedCountByRank(clearedIds);
  return counts[prev.rank] >= def.unlockClearsNeeded;
};

// テスト用に盤面定数を公開（drift 検知）。
export const DRILL_GEOMETRY = { R, TABLE_HALF_X, TABLE_HALF_Y } as const;
